import { GRID_CELL_CM, ZONE_COLORS, getZonePoints, zoneArea, SUPPORT_SNAP_CM, distToSegment, type GardenZone, type VerticalSupport } from "@/types/garden";
import type { Plant } from "@/types/plants";

const PIXELS_PER_CM = 2; // 1 cm = 2px at zoom 1

// Plant rendering — sizes relative to outer radius (derived from diameter_cm)
const PLANT_INNER_RATIO = 0.74;    // inner white disc = 74% of outer radius
const PLANT_EMOJI_RATIO = 0.68;    // emoji font = 68% of outer radius
const PLANT_LABEL_OFFSET_RATIO = 1.15; // label y = 115% of outer radius below center
const PLANT_SELECT_RATIO = 1.42;   // selection ring = 142% of outer radius
const PLANT_LABEL_SIZE = 9;
const PLANT_LABEL_COLOR = "#334430";
const PLANT_STROKE_WIDTH = 2.2;
const PLANT_FILL_OPACITY = 0.18;
const PLANT_DISC_OPACITY = 0.88;
const PLANT_MIN_OUTER_RADIUS = 8;  // minimum px at zoom=1 (for tiny plants like radis)

export interface PlacementData {
  id: string;
  zone_id: string;
  plant_id: string;
  x: number; // px bruts (pas cm)
  y: number;
  variety?: string | null;
}

export interface RenderContext {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  gardenWidthCm: number;
  gardenHeightCm: number;
  zoom: number;
  offsetX: number;
  offsetY: number;
  zones: GardenZone[];
  placements: PlacementData[];
  plants: Record<string, Plant>;
  selectedZoneId: string | null;
  selectedPlantId: string | null;
  hoveredZoneId: string | null;
  drawingRect: { x: number; y: number; w: number; h: number } | null;
  supports: VerticalSupport[];
  selectedSupportId: string | null;
  // Ghost plant preview
  ghost: { x: number; y: number; plantId: string } | null;
  // Support drawing preview (first point placed, waiting for second)
  supportPreview: { x1: number; y1: number; x2: number; y2: number } | null;
  // Animation tick for marching ants
  animTick: number;
}

/** Convert cm to canvas pixels */
function cmToPixel(cm: number, zoom: number): number {
  return cm * PIXELS_PER_CM * zoom;
}

/** Convert canvas pixel to cm */
export function pixelToCm(px: number, zoom: number): number {
  return px / (PIXELS_PER_CM * zoom);
}

/** Snap a cm value to the grid */
export function snapToGrid(cm: number): number {
  return Math.round(cm / GRID_CELL_CM) * GRID_CELL_CM;
}

/** Convert raw px position to canvas-space px (accounting for zoom) */
function rawToCanvas(rawPx: number, zoom: number): number {
  return rawPx * zoom;
}

export function render(rc: RenderContext) {
  const { ctx, width, height, zoom, offsetX, offsetY } = rc;

  // Clear
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = "#f6f3ec";
  ctx.fillRect(0, 0, width, height);

  ctx.save();
  ctx.translate(offsetX, offsetY);

  const gardenW = cmToPixel(rc.gardenWidthCm, zoom);
  const gardenH = cmToPixel(rc.gardenHeightCm, zoom);

  // Garden background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, gardenW, gardenH);

  // Grid lines
  drawGrid(ctx, rc.gardenWidthCm, rc.gardenHeightCm, zoom);

  // Zones
  for (const zone of rc.zones) {
    drawZone(ctx, zone, zoom, zone.id === rc.selectedZoneId, zone.id === rc.hoveredZoneId);
  }

  // Vertical supports (draw before plants so plants render on top)
  for (const s of rc.supports) {
    drawSupport(ctx, s, zoom, s.id === rc.selectedSupportId, rc.animTick);
  }

  // Support preview (drawing in progress)
  if (rc.supportPreview) {
    drawSupportPreview(ctx, rc.supportPreview, zoom);
  }

  // Plant placements
  for (const p of rc.placements) {
    const plant = rc.plants[p.plant_id];
    if (plant) {
      const selected = p.id === rc.selectedPlantId;
      // Check if plant is on a support (raw px → cm for distance check)
      const plantCmX = p.x / PIXELS_PER_CM;
      const plantCmY = p.y / PIXELS_PER_CM;
      const isVertical = rc.supports.some(
        (s) => distToSegment(plantCmX, plantCmY, s.x1, s.y1, s.x2, s.y2) < SUPPORT_SNAP_CM
      );
      drawPlant(ctx, p.x, p.y, plant, zoom, selected, rc.animTick, p.variety, isVertical);
    }
  }

  // Ghost plant preview
  if (rc.ghost) {
    const plant = rc.plants[rc.ghost.plantId];
    if (plant) {
      drawPlantGhost(ctx, rc.ghost.x, rc.ghost.y, plant, zoom);
    }
  }

  // Drawing preview (zone)
  if (rc.drawingRect) {
    const r = rc.drawingRect;
    ctx.strokeStyle = "#4a8a3d";
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(
      cmToPixel(r.x, zoom),
      cmToPixel(r.y, zoom),
      cmToPixel(r.w, zoom),
      cmToPixel(r.h, zoom)
    );
    ctx.setLineDash([]);

    const labelW = Math.abs(r.w);
    const labelH = Math.abs(r.h);
    if (labelW > 0 && labelH > 0) {
      const text = `${labelW}cm x ${labelH}cm`;
      ctx.font = `${12 * zoom}px system-ui`;
      ctx.fillStyle = "#4a8a3d";
      ctx.textAlign = "center";
      ctx.fillText(
        text,
        cmToPixel(r.x + r.w / 2, zoom),
        cmToPixel(r.y + r.h / 2, zoom) + 5 * zoom
      );
    }
  }

  // Garden border
  ctx.strokeStyle = "#c2c9bb";
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, gardenW, gardenH);

  // Dimensions
  ctx.font = `${11}px system-ui`;
  ctx.fillStyle = "#72796e";
  ctx.textAlign = "center";
  ctx.fillText(
    `${rc.gardenWidthCm}cm (${(rc.gardenWidthCm / 100).toFixed(1)}m)`,
    gardenW / 2,
    gardenH + 20
  );
  ctx.save();
  ctx.translate(-20, gardenH / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(
    `${rc.gardenHeightCm}cm (${(rc.gardenHeightCm / 100).toFixed(1)}m)`,
    0,
    0
  );
  ctx.restore();

  ctx.restore();
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  widthCm: number,
  heightCm: number,
  zoom: number
) {
  ctx.beginPath();
  ctx.strokeStyle = "#e5e2db";
  ctx.lineWidth = 0.5;

  for (let x = 0; x <= widthCm; x += GRID_CELL_CM) {
    const px = cmToPixel(x, zoom);
    ctx.moveTo(px, 0);
    ctx.lineTo(px, cmToPixel(heightCm, zoom));
  }
  for (let y = 0; y <= heightCm; y += GRID_CELL_CM) {
    const py = cmToPixel(y, zoom);
    ctx.moveTo(0, py);
    ctx.lineTo(cmToPixel(widthCm, zoom), py);
  }
  ctx.stroke();

  // Major grid lines every 100cm
  ctx.beginPath();
  ctx.strokeStyle = "#c2c9bb";
  ctx.lineWidth = 1;
  for (let x = 0; x <= widthCm; x += 100) {
    const px = cmToPixel(x, zoom);
    ctx.moveTo(px, 0);
    ctx.lineTo(px, cmToPixel(heightCm, zoom));
  }
  for (let y = 0; y <= heightCm; y += 100) {
    const py = cmToPixel(y, zoom);
    ctx.moveTo(0, py);
    ctx.lineTo(cmToPixel(widthCm, zoom), py);
  }
  ctx.stroke();
}

function drawZone(
  ctx: CanvasRenderingContext2D,
  zone: GardenZone,
  zoom: number,
  selected: boolean,
  hovered: boolean
) {
  const pts = getZonePoints(zone);
  const color = ZONE_COLORS[zone.color_pal] ?? ZONE_COLORS[0];

  // Convert cm points to canvas px
  const pxPts = pts.map((p) => ({
    x: cmToPixel(p.x, zoom),
    y: cmToPixel(p.y, zoom),
  }));

  // Build polygon path
  const polyPath = new Path2D();
  polyPath.moveTo(pxPts[0].x, pxPts[0].y);
  for (let i = 1; i < 4; i++) polyPath.lineTo(pxPts[i].x, pxPts[i].y);
  polyPath.closePath();

  // Fill
  ctx.fillStyle = color.bg;
  ctx.globalAlpha = 0.7;
  ctx.fill(polyPath);
  ctx.globalAlpha = 1;

  // Stroke
  ctx.strokeStyle = selected ? color.hex : hovered ? color.hex : color.hex + "80";
  ctx.lineWidth = selected ? 2.5 : hovered ? 2 : 1;
  ctx.stroke(polyPath);

  // Label — positioned at centroid of the polygon
  const cx = (pxPts[0].x + pxPts[1].x + pxPts[2].x + pxPts[3].x) / 4;
  const cy = (pxPts[0].y + pxPts[1].y + pxPts[2].y + pxPts[3].y) / 4;

  // Compute area for display
  const areaCm2 = zoneArea(pts);
  const areaM2 = (areaCm2 / 10000).toFixed(2);

  const fontSize = Math.max(10, Math.min(14, 14 * zoom));
  ctx.font = `600 ${fontSize}px system-ui`;
  ctx.fillStyle = color.hex;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(zone.name, cx, cy - fontSize * 0.4);

  ctx.font = `${Math.max(9, 10 * zoom)}px system-ui`;
  ctx.fillStyle = "#72796e";
  ctx.fillText(`${areaM2} m²`, cx, cy + fontSize * 0.6);

  // Corner handles (always visible when selected, faint when hovered)
  if (selected || hovered) {
    const handleR = selected ? 5 : 3.5;
    for (const pt of pxPts) {
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, handleR, 0, Math.PI * 2);
      ctx.fillStyle = selected ? color.hex : color.hex + "60";
      ctx.fill();
      if (selected) {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }
  }
}

/**
 * Compute the outer radius in canvas px from the plant's diameter_cm.
 */
export function plantOuterRadius(plant: Plant, zoom: number): number {
  const radiusCm = plant.diameter_cm / 2;
  const radiusPx = cmToPixel(radiusCm, zoom);
  return Math.max(radiusPx, PLANT_MIN_OUTER_RADIUS * zoom);
}

/**
 * Draw a placed plant: colored ring + white disc + emoji + label.
 * Coordinates are in raw px (stored in DB), scaled by zoom.
 * Ring size is derived from diameter_cm (real adult diameter).
 */
function drawPlant(
  ctx: CanvasRenderingContext2D,
  rawX: number,
  rawY: number,
  plant: Plant,
  zoom: number,
  selected: boolean,
  animTick: number,
  variety?: string | null,
  isVertical?: boolean
) {
  const px = rawToCanvas(rawX, zoom);
  const py = rawToCanvas(rawY, zoom);
  // Vertical plants take less ground space (60% diameter)
  const baseOuterR = plantOuterRadius(plant, zoom);
  const outerR = isVertical ? baseOuterR * 0.6 : baseOuterR;
  const innerR = outerR * PLANT_INNER_RATIO;
  const emojiSize = Math.max(10, outerR * PLANT_EMOJI_RATIO);

  // Outer ring — colored fill at 18% opacity + stroke
  ctx.beginPath();
  ctx.arc(px, py, outerR, 0, Math.PI * 2);
  ctx.fillStyle = plant.color;
  ctx.globalAlpha = PLANT_FILL_OPACITY;
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.strokeStyle = plant.color;
  ctx.lineWidth = PLANT_STROKE_WIDTH * zoom;
  ctx.stroke();

  // White disc
  ctx.beginPath();
  ctx.arc(px, py, innerR, 0, Math.PI * 2);
  ctx.fillStyle = "white";
  ctx.globalAlpha = PLANT_DISC_OPACITY;
  ctx.fill();
  ctx.globalAlpha = 1;

  // Emoji
  ctx.font = `${emojiSize}px system-ui`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(plant.emoji, px, py);

  // Vertical badge — small upward arrow above the plant
  if (isVertical) {
    const badgeY = py - outerR - 5 * zoom;
    ctx.font = `bold ${Math.max(9 * zoom, 7)}px system-ui`;
    ctx.fillStyle = "#5a8a4a";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText("\u2191", px, badgeY); // ↑
  }

  // Label
  const label = variety || plant.name;
  ctx.font = `${Math.max(PLANT_LABEL_SIZE * zoom, 7)}px system-ui`;
  ctx.fillStyle = PLANT_LABEL_COLOR;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(label, px, py + outerR * PLANT_LABEL_OFFSET_RATIO);

  // Selection — marching ants
  if (selected) {
    ctx.beginPath();
    ctx.arc(px, py, outerR * PLANT_SELECT_RATIO, 0, Math.PI * 2);
    ctx.strokeStyle = plant.color;
    ctx.lineWidth = 1.5 * zoom;
    ctx.setLineDash([5, 3]);
    ctx.lineDashOffset = -animTick;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;
  }
}

/**
 * Draw ghost preview — semi-transparent ring + emoji + dashed ring.
 */
function drawPlantGhost(
  ctx: CanvasRenderingContext2D,
  rawX: number,
  rawY: number,
  plant: Plant,
  zoom: number
) {
  const px = rawToCanvas(rawX, zoom);
  const py = rawToCanvas(rawY, zoom);
  const outerR = plantOuterRadius(plant, zoom);
  const innerR = outerR * PLANT_INNER_RATIO;
  const emojiSize = Math.max(10, outerR * PLANT_EMOJI_RATIO);

  // Dashed outer ring
  ctx.beginPath();
  ctx.arc(px, py, outerR, 0, Math.PI * 2);
  ctx.strokeStyle = plant.color;
  ctx.lineWidth = PLANT_STROKE_WIDTH * zoom;
  ctx.globalAlpha = 0.5;
  ctx.setLineDash([4, 3]);
  ctx.stroke();
  ctx.setLineDash([]);

  // Faint fill
  ctx.beginPath();
  ctx.arc(px, py, outerR, 0, Math.PI * 2);
  ctx.fillStyle = plant.color;
  ctx.globalAlpha = 0.1;
  ctx.fill();

  // White disc
  ctx.beginPath();
  ctx.arc(px, py, innerR, 0, Math.PI * 2);
  ctx.fillStyle = "white";
  ctx.globalAlpha = 0.6;
  ctx.fill();
  ctx.globalAlpha = 1;

  // Emoji
  ctx.globalAlpha = 0.7;
  ctx.font = `${emojiSize}px system-ui`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(plant.emoji, px, py);
  ctx.globalAlpha = 1;
}

/**
 * Draw a vertical support — thick line with cross-hatching + shadow projection.
 */
function drawSupport(
  ctx: CanvasRenderingContext2D,
  support: VerticalSupport,
  zoom: number,
  selected: boolean,
  animTick: number
) {
  const x1 = cmToPixel(support.x1, zoom);
  const y1 = cmToPixel(support.y1, zoom);
  const x2 = cmToPixel(support.x2, zoom);
  const y2 = cmToPixel(support.y2, zoom);

  // Shadow projection (offset down-right, proportional to height, in cm)
  const shadowOff = cmToPixel(Math.min(support.height_cm * 0.12, 25), zoom);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x2 + shadowOff, y2 + shadowOff);
  ctx.lineTo(x1 + shadowOff, y1 + shadowOff);
  ctx.closePath();
  ctx.fillStyle = "rgba(35, 80, 30, 0.06)";
  ctx.fill();

  // Visual style per support type — widthCm is real-world thickness
  const styles: Record<string, { color: string; inner: string; dash: number[]; widthCm: number; postCm: number }> = {
    treillis:  { color: "#6a5a3a", inner: "#8a7a5a", dash: [6, 5],         widthCm: 3,  postCm: 2.5 },
    tuteur:    { color: "#7a6a4a", inner: "#9a8a6a", dash: [2, 8],         widthCm: 1.5, postCm: 1.5 },
    pergola:   { color: "#5a4a3a", inner: "#7a6a5a", dash: [10, 4],        widthCm: 8,  postCm: 5 },
    palette:   { color: "#8a7050", inner: "#a08a6a", dash: [3, 2.5, 1, 2.5], widthCm: 5,  postCm: 2 },
  };
  const st = styles[support.type] ?? styles.treillis;

  // Convert cm widths to canvas px
  const lineWidth = Math.max(2, cmToPixel(st.widthCm, zoom));
  const postR = Math.max(1.5, cmToPixel(st.postCm, zoom));

  // Main line
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = st.color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.stroke();

  // Inner hatching (dash values in cm, converted to px)
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = st.inner;
  ctx.lineWidth = lineWidth * 0.4;
  ctx.setLineDash(st.dash.map((d) => cmToPixel(d, zoom)));
  ctx.stroke();
  ctx.setLineDash([]);

  // End circles (posts)
  for (const [cx, cy] of [[x1, y1], [x2, y2]]) {
    ctx.beginPath();
    ctx.arc(cx, cy, postR, 0, Math.PI * 2);
    ctx.fillStyle = st.color;
    ctx.fill();
  }

  // Label — only if big enough to read
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const fontSize = Math.max(7, Math.min(11, 9 * zoom));
  if (fontSize >= 7) {
    ctx.font = `${fontSize}px system-ui`;
    ctx.fillStyle = st.color;
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(`${support.height_cm}cm \u2191`, mx, my - lineWidth - 2);

    ctx.textBaseline = "top";
    ctx.fillText(support.name, mx, my + lineWidth + 2);
  }

  // Selection — marching ants outside the line
  if (selected) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = "#4a8a3d";
    ctx.lineWidth = lineWidth + 6;
    ctx.lineCap = "round";
    ctx.setLineDash([10, 8]);
    ctx.lineDashOffset = -animTick;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;
  }
}

/**
 * Draw support preview while placing (dashed line from first point to cursor).
 */
function drawSupportPreview(
  ctx: CanvasRenderingContext2D,
  preview: { x1: number; y1: number; x2: number; y2: number },
  zoom: number
) {
  const x1 = cmToPixel(preview.x1, zoom);
  const y1 = cmToPixel(preview.y1, zoom);
  const x2 = cmToPixel(preview.x2, zoom);
  const y2 = cmToPixel(preview.y2, zoom);

  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = "#6a5a3a";
  ctx.lineWidth = 4 * zoom;
  ctx.lineCap = "round";
  ctx.globalAlpha = 0.5;
  ctx.setLineDash([6, 4]);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;

  // Start point
  ctx.beginPath();
  ctx.arc(x1, y1, 4 * zoom, 0, Math.PI * 2);
  ctx.fillStyle = "#5a4a2a";
  ctx.fill();

  // Length label
  const lenCm = Math.round(Math.hypot(preview.x2 - preview.x1, preview.y2 - preview.y1));
  if (lenCm > 0) {
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    ctx.font = `${Math.max(10, 11 * zoom)}px system-ui`;
    ctx.fillStyle = "#6a5a3a";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    ctx.fillText(`${lenCm}cm`, mx, my - 8 * zoom);
  }
}
